import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'away' | 'offline';
  currentProjectId?: string;
  projectsCount: number;
  createdAt: string;
}

interface TeamContextType {
  teamMembers: TeamMember[];
  setTeamMembers: React.Dispatch<React.SetStateAction<TeamMember[]>>;
  addTeamMember: (member: Omit<TeamMember, 'id' | 'createdAt' | 'projectsCount'>) => Promise<string | null>;
  updateTeamMember: (id: string, updates: Partial<TeamMember>) => Promise<void>;
  deleteTeamMember: (id: string) => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export function TeamProvider({ children }: { children: ReactNode }) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  const addTeamMember = async (
    member: Omit<TeamMember, 'id' | 'createdAt' | 'projectsCount'>
  ): Promise<string | null> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('team_members')
        .insert([{
          name: member.name,
          email: member.email,
          role: member.role,
          status: member.status,
          current_project_id: member.currentProjectId,
          projects_count: 0,
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding team member to Supabase:', error);
        return null;
      }

      if (data) {
        const newMember: TeamMember = {
          id: data.id,
          name: data.name,
          email: data.email,
          role: data.role,
          status: data.status,
          currentProjectId: data.current_project_id,
          projectsCount: data.projects_count || 0,
          createdAt: data.created_at,
        };
        setTeamMembers((prev) => [...prev, newMember]);
        return data.id;
      }
    } else {
      const newMember: TeamMember = {
        id: `team-${Date.now()}-${Math.random()}`,
        ...member,
        projectsCount: 0,
        createdAt: new Date().toISOString(),
      };
      setTeamMembers((prev) => [...prev, newMember]);
      return newMember.id;
    }
    return null;
  };

  const updateTeamMember = async (id: string, updates: Partial<TeamMember>) => {
    if (supabase) {
      const { error } = await supabase
        .from('team_members')
        .update({
          name: updates.name,
          email: updates.email,
          role: updates.role,
          status: updates.status,
          current_project_id: updates.currentProjectId,
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating team member:', error);
        return;
      }
    }

    setTeamMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...updates } : m))
    );
  };

  const deleteTeamMember = async (id: string) => {
    if (supabase) {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting team member:', error);
        return;
      }
    }

    setTeamMembers((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <TeamContext.Provider
      value={{
        teamMembers,
        setTeamMembers,
        addTeamMember,
        updateTeamMember,
        deleteTeamMember,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}





